import React, { useState } from 'react';
import { VscVerifiedFilled } from "react-icons/vsc";
import { FaGlobe } from "react-icons/fa";

interface StoreCardProps {
  imageUrl: string;
  logoUrl: string;
  storeName: string;
  description: string;
  verified?: boolean;
  urls: {
    label: string;
    url: string;
  }[];
}

const StoreCard: React.FC<StoreCardProps> = ({ 
  imageUrl, 
  logoUrl, 
  storeName, 
  description, 
  verified = false,
  urls
}) => {
  const [isImageError, setIsImageError] = useState(false);
  const [isLogoError, setIsLogoError] = useState(false);

  return (
    <div className="group relative w-full max-w-sm mx-auto bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1">
      {/* Main image */}
      <div className="relative w-full aspect-[4/3]">
        {!isImageError ? (
          <img
            src={imageUrl}
            alt={storeName}
            className="w-full h-full object-cover"
            onError={() => {
              console.log('Image load error:', imageUrl);
              setIsImageError(true);
            }}
          />
        ) : (
          <div className="w-full h-full bg-gray-50 flex justify-center items-center">
            <span className="text-gray-400">스토어 이미지 없음</span>
          </div>
        )}
        
        {/* 항상 보이는 그라데이션 오버레이 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Content container */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex items-center gap-3 mb-2">
          {/* Logo */}
          {!isLogoError ? (
            <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 border-white shadow-lg">
              <img
                src={logoUrl}
                alt="Store Logo"
                className="w-full h-full object-cover"
                onError={() => {
                  console.log('Logo load error:', logoUrl);
                  setIsLogoError(true);
                }}
              />
            </div>
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-lg border-2 border-white shadow-lg flex items-center justify-center">
              <span className="text-white text-lg font-bold">{storeName.charAt(0)}</span>
            </div>
          )}

          {/* Store Info */}
          <div className="flex-grow">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold Pretendard-Bold truncate">
                {storeName}
              </h3>
              {verified && (
                <VscVerifiedFilled className="text-blue-400 flex-shrink-0" size={16} />
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {urls.map((link, index) => (
                <a 
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors"
                >
                  <FaGlobe size={12} />
                  <span>{link.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-sm text-gray-200 Pretendard-Regular line-clamp-2">
          {description}
        </p>
      </div>
    </div>
  );
};

export default StoreCard; 